import React from 'react';

import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import MenuItem from '@mui/material/MenuItem';

import Record from 'ui/Record/Record';
import EntitiesDetailsLoader from 'ui/DetailsLoader/EntitiesDetailsLoader';

import { Button } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';
import CustomModal from 'components/CustomModal';
import EntityDetailsContent from 'components/Entities/EntityDetailsContent';
import EntityDetailsHeader from 'components/Entities/EntityDetailsHeader';
import ArchivedPill from 'components/ArchivedPill';
import UserRow from 'components/UserRow';

import DataError from 'containers/Error/DataError';
import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import { isNotFound } from 'utils/api/errors';
import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';
import { formatLibraryId } from 'utils/formatters/libraryId';
import { canArchiveLibrary } from 'utils/roles/permissions';
import { UuidableName } from 'utils/api/types';
import { ChoiceValue } from 'utils/library';

import { EntityModules } from 'core/constants';

import 'containers/Controls/entityFieldConfig';
import { ARCHIVE_CONFIRMATION_MESSAGE, CONTROL_FIELD, ControlStatus } from 'containers/Controls/constants';
import ControlStatusChip from 'containers/Controls/components/ControlStatusChip';

const ControlDetails: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const params = useParams<{ entityId: string }>();
  const { controlDetails, session } = useStore();
  const [dialog, setDialog] = React.useState<'archive' | 'in-use' | null>(null);
  const [busy, setBusy] = React.useState(false);

  const canArchive = canArchiveLibrary(session.user) && !session.user?.readOnly;

  const setArchived = async (archived: boolean): Promise<void> => {
    setBusy(true);
    await controlDetails.setArchived(archived);
    setBusy(false);
    setDialog(null);
  };

  return (
    <EntitiesDetailsLoader params={params} routeParam="entityId" store={controlDetails}>
      {({ isLoaded, error, reload }): React.ReactElement => {
        if (!isLoaded) {
          return <CenteredCircularProgress />;
        }
        if (error) {
          return isNotFound(error) ? <NotFound /> : <DataError onReload={reload} />;
        }
        if (controlDetails.details === undefined) {
          return <NotFound />;
        }

        const { details } = controlDetails;
        const valueOf = (key: string): unknown => details.entity[key]?.value;
        const choiceKey = (key: string): string | undefined => (valueOf(key) as ChoiceValue)?.[0]?.key;
        const choiceLabel = (key: string): string => (valueOf(key) as ChoiceValue)?.[0]?.value ?? '-';
        const status = choiceKey(CONTROL_FIELD.status) as ControlStatus | undefined;
        const owner = (valueOf(CONTROL_FIELD.owner) as UuidableName | null) ?? null;
        const archived = Boolean(details.archivedAt);

        return (
          <>
            <Record
              navBarHeight={navBarHeight}
              Header={
                <EntityDetailsHeader
                  store={controlDetails}
                  title={controlDetails.getName(details)}
                  menuTestId="control-record-menu"
                  canDelete={!details.inUse}
                  breadcrumbs={[
                    { label: 'Controls library', link: controlDetails.path },
                    {
                      label: formatLibraryId(EntityModules.Controls, valueOf(CONTROL_FIELD.id) as number | null),
                      link: controlDetails.detailsPath(details.uuid),
                    },
                  ]}
                  onDeleteFailure={() => {
                    if (!controlDetails.deleteBlockedReason) {
                      return false;
                    }
                    setDialog('in-use');
                    return true;
                  }}
                  menuItems={
                    canArchive
                      ? (close) =>
                          archived ? (
                            <MenuItem
                              data-testid="control-unarchive"
                              onClick={() => {
                                close();
                                setArchived(false);
                              }}
                            >
                              Unarchive
                            </MenuItem>
                          ) : (
                            <MenuItem
                              data-testid="control-archive"
                              onClick={() => {
                                close();
                                setDialog('archive');
                              }}
                            >
                              Archive
                            </MenuItem>
                          )
                      : undefined
                  }
                  columns={[
                    {
                      name: 'ID',
                      value: (
                        <p>{formatLibraryId(EntityModules.Controls, valueOf(CONTROL_FIELD.id) as number | null)}</p>
                      ),
                    },
                    {
                      name: 'Status',
                      value: archived ? <ArchivedPill /> : status ? <ControlStatusChip status={status} /> : <p>-</p>,
                    },
                    { name: 'Category', value: <p>{choiceLabel(CONTROL_FIELD.category)}</p> },
                    { name: 'Owner', value: <UserRow user={owner} /> },
                  ]}
                />
              }
              Content={(onError) => <EntityDetailsContent onError={onError} store={controlDetails} />}
            />
            <CustomModal
              openModal={dialog === 'archive'}
              onClose={() => setDialog(null)}
              modalTitle="Archive this control?"
              modalDescription={ARCHIVE_CONFIRMATION_MESSAGE}
              cancelButton={
                <Button variant="outlined" size="large" onClick={() => setDialog(null)}>
                  Cancel
                </Button>
              }
              submitButton={
                <Button variant="contained" size="large" disabled={busy} onClick={() => setArchived(true)}>
                  Archive
                </Button>
              }
            />
            <CustomModal
              openModal={dialog === 'in-use'}
              onClose={() => setDialog(null)}
              modalTitle="This control is in use"
              modalDescription={`${controlDetails.deleteBlockedReason ?? ''} Archive it instead, so it stays where it is used but cannot be added again.`}
              cancelButton={
                <Button variant="outlined" size="large" onClick={() => setDialog(null)}>
                  Cancel
                </Button>
              }
              submitButton={
                <Button variant="contained" size="large" disabled={busy || archived} onClick={() => setArchived(true)}>
                  Archive
                </Button>
              }
            />
          </>
        );
      }}
    </EntitiesDetailsLoader>
  );
};

export default observer(ControlDetails);
